/**
 * Prediction Modal - Full flow with auth
 */

import { getTomorrowDate, isValidEthAddress } from './storage';
import { getUser, loginWithEmail, verifyEmailCode, logout, getAuthToken, fetchUserProfile, saveWalletToBackend, type AuthUser, type PredictionData, type UserStats } from './auth';
import { createPricePicker, getPickerState } from './price-picker';

export type ModalState = 'loading' | 'login' | 'otp' | 'predict' | 'confirmed';

export interface ModalCallbacks {
  onClose: () => void;
  onStateChange: (state: ModalState) => void;
}

let currentState: ModalState = 'loading';
let currentUser: AuthUser | null = null;
let pendingEmail: string | null = null;
let errorMessage: string | null = null;
let currentEthPrice: number = 0;
let currentWallet: string | null = null;
let currentPrediction: PredictionData | null = null;
let currentStats: UserStats | null = null;

/**
 * LOCAL TESTING: Mock settled prediction data
 * Usage: Add ?mockSettled=winner|correct|wrong to URL
 */
function getMockSettledData(): { prediction: PredictionData; stats: UserStats } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const mockType = params.get('mockSettled');
  if (!mockType) return null;

  const basePrediction: PredictionData = {
    price: 2450,
    percentChange: 2.5,
    tier: 'UNCOMMON',
    settled: true,
    actualClose: 2478,
    priceDiff: 28,
    percentOff: 1.13,
    correct: true,
    isWinner: false,
  };

  const baseStats: UserStats = {
    totalPredictions: 12,
    correctPredictions: 9,
    wins: 2,
    currentStreak: 3,
    longestStreak: 5,
    score: 450,
    settledPredictions: 12,
    avgPercentOff: 1.82,
  };

  switch (mockType) {
    case 'winner':
      return {
        prediction: { ...basePrediction, isWinner: true, priceDiff: 3, percentOff: 0.12 },
        stats: baseStats,
      };
    case 'correct':
      return {
        prediction: { ...basePrediction, correct: true, isWinner: false },
        stats: baseStats,
      };
    case 'wrong':
      return {
        prediction: { 
          ...basePrediction, 
          correct: false, 
          isWinner: false,
          price: 2300,
          percentChange: -3.5,
          priceDiff: 178,
          percentOff: 7.18,
        },
        stats: { ...baseStats, correctPredictions: 8 },
      };
    case 'pending':
      // Unsettled prediction (waiting for tomorrow)
      return {
        prediction: {
          price: 2500,
          percentChange: 1.8,
          tier: 'COMMON',
        },
        stats: baseStats,
      };
    default:
      return null;
  }
}

/**
 * Set current ETH price (call before opening modal)
 */
export function setCurrentPrice(price: number): void {
  currentEthPrice = price;
}

/**
 * Get dynamic button label based on user's prediction state
 * Call on page load to update the predict button text
 */
export async function getPredictButtonLabel(): Promise<string> {
  // Check for mock mode first
  const mockData = getMockSettledData();
  if (mockData) {
    return mockData.prediction.settled ? 'VIEW RESULT' : 'VIEW PREDICTION';
  }

  try {
    const user = await getUser();
    if (!user) {
      return 'PREDICT TOMORROW';
    }

    const profile = await fetchUserProfile();
    if (!profile?.prediction) {
      return 'PREDICT TOMORROW';
    }

    // User has a prediction - check if settled
    if (profile.prediction.settled) {
      return 'VIEW RESULT';
    }

    return 'VIEW PREDICTION';
  } catch {
    return 'PREDICT TOMORROW';
  }
}

/**
 * Initialize modal - check auth state
 */
export async function initModal(): Promise<ModalState> {
  currentState = 'loading';
  errorMessage = null;
  
  // LOCAL TESTING: Check for mock data first
  const mockData = getMockSettledData();
  if (mockData) {
    console.log('[Modal] Using mock data:', mockData);
    currentUser = { id: 'mock-user', email: 'test@example.com' };
    currentWallet = '0x1234567890abcdef1234567890abcdef12345678';
    currentPrediction = mockData.prediction;
    currentStats = mockData.stats;
    currentState = 'confirmed';
    return currentState;
  }
  
  try {
    currentUser = await getUser();
    
    if (currentUser) {
      // Fetch profile from backend (includes wallet, prediction, and stats)
      const profile = await fetchUserProfile();
      currentWallet = currentUser.wallet || profile?.wallet || null;
      currentPrediction = profile?.prediction || null;
      currentStats = profile?.stats || null;
      
      // Logged in - check if already predicted
      currentState = currentPrediction ? 'confirmed' : 'predict';
    } else {
      currentState = 'login';
    }
  } catch (err) {
    console.error('[Modal] Init error:', err);
    currentState = 'login';
  }
  
  return currentState;
}

/**
 * Handle email submission
 */
export async function submitEmail(email: string): Promise<boolean> {
  errorMessage = null;
  try {
    await loginWithEmail(email);
    pendingEmail = email;
    currentState = 'otp';
    return true;
  } catch (err) {
    console.error('[Modal] Email error:', err);
    errorMessage = 'Failed to send code. Try again.';
    return false;
  }
}

/**
 * Handle OTP verification
 */
export async function submitOtp(code: string): Promise<boolean> {
  if (!pendingEmail) return false;
  errorMessage = null;
  
  try {
    currentUser = await verifyEmailCode(pendingEmail, code);
    pendingEmail = null;
    
    // Fetch profile from backend (includes wallet, prediction, and stats)
    const profile = await fetchUserProfile();
    currentWallet = currentUser.wallet || profile?.wallet || null;
    currentPrediction = profile?.prediction || null;
    currentStats = profile?.stats || null;
    
    // Check if already predicted
    currentState = currentPrediction ? 'confirmed' : 'predict';
    return true;
  } catch (err) {
    console.error('[Modal] OTP error:', err);
    errorMessage = 'Invalid code. Try again.';
    return false;
  }
}

/**
 * Handle prediction submission
 */
export async function submitPrediction(): Promise<boolean> {
  const pickerState = getPickerState();
  if (!pickerState) return false;

  const wallet = currentWallet;
  
  // Wallet required for NFT delivery
  if (!wallet) {
    errorMessage = 'Please link your wallet first to receive NFT if you win.';
    return false;
  }

  const token = await getAuthToken();

  if (!token) {
    errorMessage = 'Session expired. Please sign in again.';
    currentState = 'login';
    return false;
  }

  try {
    const response = await fetch('/api/predictions/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        price: pickerState.selectedPrice,
        percentChange: pickerState.selectedPercent,
        tier: pickerState.selectedTier,
        targetDate: getTomorrowDate(),
        wallet: wallet || undefined,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      errorMessage = data.error || 'Failed to submit prediction';
      return false;
    }

    // Store prediction for display
    currentPrediction = {
      price: pickerState.selectedPrice,
      percentChange: pickerState.selectedPercent,
      tier: pickerState.selectedTier,
    };
    currentState = 'confirmed';
    return true;
  } catch (err) {
    console.error('[Modal] Submit error:', err);
    errorMessage = 'Network error. Please try again.';
    return false;
  }
}

/**
 * Handle logout
 */
export async function handleLogout(): Promise<void> {
  await logout();
  currentUser = null;
  currentWallet = null;
  currentPrediction = null;
  currentStats = null;
  currentState = 'login';
}

/**
 * Reset transient state (call on modal close)
 */
export function resetModalState(): void {
  errorMessage = null;
  pendingEmail = null;
  // Keep user session data (currentUser, currentWallet, currentPrediction)
}

/**
 * Render modal HTML based on current state
 */
export function renderModal(): string {
  const tomorrowDate = getTomorrowDate();
  
  const overlay = (content: string, titleId: string = 'modal-title') => `
    <div class="prediction-overlay" id="prediction-overlay" role="dialog" aria-modal="true" aria-labelledby="${titleId}">
      <div class="prediction-modal">
        <button class="modal-close" id="modal-close" aria-label="Close modal" type="button">×</button>
        ${content}
      </div>
    </div>
  `;

  // Loading state
  if (currentState === 'loading') {
    return overlay(`
      <h3 class="modal-title" id="modal-title">LOADING...</h3>
    `);
  }

  // Login state - email input
  if (currentState === 'login') {
    return overlay(`
      <h3 class="modal-title" id="modal-title">PREDICT TOMORROW</h3>
      <p class="modal-subtitle">Sign in to make your prediction</p>
      
      <form class="auth-form" id="email-form">
        <input 
          type="email" 
          id="email-input" 
          class="auth-input"
          placeholder="your@email.com"
          required
          autocomplete="email"
        />
        <button type="submit" class="auth-btn">SEND CODE</button>
      </form>
      
      ${errorMessage ? `<p class="auth-error">${errorMessage}</p>` : ''}
    `);
  }

  // OTP state - code input
  if (currentState === 'otp') {
    return overlay(`
      <h3 class="modal-title" id="modal-title">CHECK YOUR EMAIL</h3>
      <p class="modal-subtitle">Enter the code sent to<br><strong>${pendingEmail}</strong></p>
      
      <form class="auth-form" id="otp-form">
        <input 
          type="text" 
          id="otp-input" 
          class="auth-input"
          placeholder="000000"
          maxlength="6"
          pattern="[0-9]*"
          inputmode="numeric"
          autocomplete="one-time-code"
          required
        />
        <button type="submit" class="auth-btn">VERIFY</button>
      </form>
      
      ${errorMessage ? `<p class="auth-error">${errorMessage}</p>` : ''}
      
      <div style="display: flex; gap: var(--space-md); justify-content: center; margin-top: var(--space-md);">
        <button class="auth-link" id="resend-btn">Resend code</button>
        <button class="auth-link" id="back-btn">← Different email</button>
      </div>
    `);
  }

  // Predict state - Price picker
  if (currentState === 'predict') {
    const wallet = currentWallet;
    const hasWallet = !!wallet;
    
    const walletSection = wallet 
      ? `<p class="wallet-linked">✓ Wallet: ${wallet.slice(0, 6)}...${wallet.slice(-4)}</p>
         <button class="auth-link" id="change-wallet-btn" type="button">Change wallet</button>`
      : `<p class="wallet-required">⚠️ Wallet required to receive NFT if you win</p>
         <button class="auth-btn auth-btn--small" id="link-wallet-btn" type="button">+ LINK WALLET</button>`;
    
    return `
    <div class="prediction-overlay" id="prediction-overlay" role="dialog" aria-modal="true" aria-labelledby="picker-title">
      <div class="prediction-modal prediction-modal--wide">
        <button class="modal-close" id="modal-close" aria-label="Close modal" type="button">×</button>
        
        <div id="price-picker-container"></div>
        
        <div class="wallet-section">
          ${walletSection}
        </div>
        
        <div class="wallet-form hidden" id="wallet-form">
          <input 
            type="text" 
            id="wallet-input" 
            class="auth-input"
            placeholder="0x..."
            pattern="0x[a-fA-F0-9]{40}"
            aria-describedby="wallet-error"
          />
          <button class="auth-btn" id="save-wallet-btn" type="button">SAVE</button>
        </div>
        <p class="auth-error hidden" id="wallet-error">Invalid address (must be 0x + 40 hex characters)</p>
        
        <button class="predict-submit-btn${hasWallet ? '' : ' predict-submit-btn--disabled'}" id="predict-submit" type="button" ${hasWallet ? '' : 'disabled'}>
          ${hasWallet ? 'LOCK IN PREDICTION' : 'LINK WALLET TO PREDICT'}
        </button>
        
        <p class="auth-note">Signed in as ${currentUser?.email || 'user'}</p>
        <button class="auth-link" id="logout-btn" type="button">Sign out</button>
      </div>
    </div>
    `;
  }

  // Confirmed state - show prediction (with accuracy feedback if settled)
  if (currentState === 'confirmed') {
    const prediction = currentPrediction;
    const isUp = (prediction?.percentChange || 0) >= 0;
    const priceFormatted = prediction?.price ? '$' + prediction.price.toLocaleString() : '--';
    const percentFormatted = prediction?.percentChange !== undefined
      ? (isUp ? '+' : '') + prediction.percentChange.toFixed(2) + '%'
      : '--';
    const tier = prediction?.tier || 'COMMON';
    const wallet = currentWallet;
    const walletSection = wallet 
      ? `<p class="wallet-linked">Wallet: ${wallet.slice(0, 6)}...${wallet.slice(-4)}</p>
         <button class="auth-link" id="change-wallet-btn">Change wallet</button>`
      : `<button class="auth-link" id="link-wallet-btn">+ Link wallet for rewards</button>`;
    
    // Accuracy feedback (only shown if prediction has been settled)
    let accuracyFeedback = '';
    if (prediction?.settled) {
      const actualFormatted = prediction.actualClose ? '$' + prediction.actualClose.toLocaleString() : '--';
      const diffFormatted = prediction.priceDiff ? '$' + prediction.priceDiff.toLocaleString() : '--';
      const percentOffFormatted = prediction.percentOff !== undefined ? prediction.percentOff.toFixed(2) + '%' : '--';
      
      const resultClass = prediction.isWinner ? 'winner' : (prediction.correct ? 'correct' : 'incorrect');
      const resultText = prediction.isWinner 
        ? 'WINNER' 
        : (prediction.correct ? 'DIRECTION CORRECT' : 'DIRECTION WRONG');
      
      accuracyFeedback = `
        <div class="accuracy-feedback ${resultClass}">
          <div class="accuracy-result">${resultText}</div>
          <div class="accuracy-details">
            <div class="accuracy-row">
              <span class="accuracy-label">Your prediction</span>
              <span class="accuracy-value">${priceFormatted}</span>
            </div>
            <div class="accuracy-row">
              <span class="accuracy-label">Actual close</span>
              <span class="accuracy-value">${actualFormatted}</span>
            </div>
            <div class="accuracy-row">
              <span class="accuracy-label">Off by</span>
              <span class="accuracy-value highlight">${diffFormatted} (${percentOffFormatted})</span>
            </div>
          </div>
        </div>
      `;
    }
    
    // User stats section (accuracy over time)
    let statsSection = '';
    if (currentStats && currentStats.settledPredictions > 0) {
      const avgOff = currentStats.avgPercentOff !== null ? currentStats.avgPercentOff.toFixed(2) + '%' : '--';
      const accuracy = currentStats.totalPredictions > 0 
        ? Math.round((currentStats.correctPredictions / currentStats.totalPredictions) * 100) + '%'
        : '--';
      
      statsSection = `
        <div class="user-stats">
          <div class="user-stats-title">Your accuracy</div>
          <div class="user-stats-grid">
            <div class="user-stat">
              <span class="user-stat-value">${currentStats.settledPredictions}</span>
              <span class="user-stat-label">Settled</span>
            </div>
            <div class="user-stat">
              <span class="user-stat-value">${avgOff}</span>
              <span class="user-stat-label">Avg off</span>
            </div>
            <div class="user-stat">
              <span class="user-stat-value">${accuracy}</span>
              <span class="user-stat-label">Direction</span>
            </div>
            <div class="user-stat">
              <span class="user-stat-value">${currentStats.wins}</span>
              <span class="user-stat-label">Wins</span>
            </div>
          </div>
        </div>
      `;
    }
    
    const title = prediction?.settled ? 'PREDICTION RESULT' : 'PREDICTION LOCKED';
    const subtitle = prediction?.settled 
      ? 'How close were you?'
      : `Your prediction for ${tomorrowDate}`;
    const note = prediction?.settled 
      ? '' 
      : '<p class="modal-note">Check back tomorrow to see if you were closest!</p>';
    
    return overlay(`
      <h3 class="modal-title" id="modal-title">${title}</h3>
      <p class="modal-subtitle">${subtitle}</p>
      
      ${accuracyFeedback}
      
      ${!prediction?.settled ? `
      <div class="prediction-confirmed-price">
        <div class="confirmed-price">${priceFormatted}</div>
        <div class="confirmed-details">
          <span class="confirmed-percent ${isUp ? 'up' : 'down'}">${percentFormatted}</span>
          <span class="confirmed-tier ${tier.toLowerCase()}">${tier}</span>
        </div>
      </div>
      ` : ''}
      
      ${note}
      
      ${/* Test tools - shown in dev mode OR with ?predict URL param */ ''}
      ${(import.meta.env.DEV || new URLSearchParams(window.location.search).has('predict')) ? `
      <div class="test-settle-section">
        <div class="test-settle-title">Test Tools</div>
        ${!prediction?.settled ? `
        <div class="test-settle-form">
          <input 
            type="number" 
            id="test-close-input" 
            class="auth-input"
            placeholder="Actual close price"
            step="0.01"
          />
          <button class="auth-btn" id="test-settle-btn" type="button">SETTLE</button>
        </div>
        <p class="test-settle-hint">Enter a close price to instantly settle your prediction</p>
        ` : ''}
        <button class="auth-btn" id="test-reset-btn" type="button" style="margin-top: 8px; background: #ff4444;">RESET PREDICTION</button>
        <p class="test-settle-hint">Delete prediction and result to test again</p>
      </div>
      ` : ''}
      
      ${statsSection}
      
      <div class="wallet-section">
        ${walletSection}
      </div>
      
      <div class="wallet-form hidden" id="wallet-form">
        <input 
          type="text" 
          id="wallet-input" 
          class="auth-input"
          placeholder="0x..."
          pattern="0x[a-fA-F0-9]{40}"
          aria-describedby="wallet-error"
        />
        <button class="auth-btn" id="save-wallet-btn" type="button">SAVE</button>
      </div>
      <p class="auth-error hidden" id="wallet-error">Invalid address (must be 0x + 40 hex characters)</p>
      
      <p class="auth-note">Signed in as ${currentUser?.email || 'user'}</p>
      <button class="auth-link" id="logout-btn" type="button">Sign out</button>
    `);
  }

  return '';
}

/**
 * Bind modal events
 */
export function bindModalEvents(callbacks: ModalCallbacks): void {
  const overlay = document.getElementById('prediction-overlay');
  const closeBtn = document.getElementById('modal-close');
  const modal = overlay?.querySelector('.prediction-modal') as HTMLElement;
  
  // Lock body scroll
  document.body.style.overflow = 'hidden';
  
  // Close handlers
  const handleClose = () => {
    document.body.style.overflow = '';
    callbacks.onClose();
  };
  
  closeBtn?.addEventListener('click', handleClose);
  overlay?.addEventListener('click', (e) => {
    if (e.target === overlay) handleClose();
  });
  
  // Escape key to close
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      document.removeEventListener('keydown', handleEscape);
      handleClose();
    }
  };
  document.addEventListener('keydown', handleEscape);
  
  // Focus trap
  if (modal) {
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    // Focus first element
    firstFocusable?.focus();
    
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    });
  }

  // Email form
  const emailForm = document.getElementById('email-form');
  emailForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('email-input') as HTMLInputElement;
    if (input?.value) {
      const btn = emailForm.querySelector('button') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'SENDING...';
      
      await submitEmail(input.value);
      callbacks.onStateChange(currentState);
    }
  });

  // OTP form
  const otpForm = document.getElementById('otp-form');
  otpForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('otp-input') as HTMLInputElement;
    if (input?.value) {
      const btn = otpForm.querySelector('button') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'VERIFYING...';
      
      await submitOtp(input.value);
      callbacks.onStateChange(currentState);
    }
  });

  // Back button (OTP → Login)
  const backBtn = document.getElementById('back-btn');
  backBtn?.addEventListener('click', () => {
    pendingEmail = null;
    errorMessage = null;
    currentState = 'login';
    callbacks.onStateChange(currentState);
  });

  // Resend code button
  const resendBtn = document.getElementById('resend-btn');
  resendBtn?.addEventListener('click', async () => {
    if (!pendingEmail) return;
    if (resendBtn instanceof HTMLButtonElement) {
      resendBtn.disabled = true;
      resendBtn.textContent = 'Sending...';
    }
    try {
      await loginWithEmail(pendingEmail);
      if (resendBtn instanceof HTMLButtonElement) {
        resendBtn.textContent = 'Sent!';
        setTimeout(() => {
          resendBtn.disabled = false;
          resendBtn.textContent = 'Resend code';
        }, 3000);
      }
    } catch {
      if (resendBtn instanceof HTMLButtonElement) {
        resendBtn.disabled = false;
        resendBtn.textContent = 'Resend code';
      }
      errorMessage = 'Failed to resend. Try again.';
      callbacks.onStateChange(currentState);
    }
  });

  // Price picker initialization
  const pickerContainer = document.getElementById('price-picker-container');
  const submitBtn = document.getElementById('predict-submit') as HTMLButtonElement;
  
  if (pickerContainer) {
    const picker = createPricePicker(pickerContainer, {
      currentPrice: currentEthPrice,
    });
    
    // Disable submit if picker failed to initialize
    if (!picker && submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'PRICE UNAVAILABLE';
    }
  }

  // Submit prediction button (submitBtn already declared above)
  if (submitBtn && !submitBtn.disabled) {
    submitBtn.addEventListener('click', async () => {
      submitBtn.disabled = true;
      submitBtn.textContent = 'SUBMITTING...';
      await submitPrediction();
      callbacks.onStateChange(currentState);
    });
  }

  // Logout button
  const logoutBtn = document.getElementById('logout-btn');
  logoutBtn?.addEventListener('click', async () => {
    await handleLogout();
    callbacks.onStateChange(currentState);
  });

  // Wallet linking
  const linkWalletBtn = document.getElementById('link-wallet-btn');
  const changeWalletBtn = document.getElementById('change-wallet-btn');
  const walletForm = document.getElementById('wallet-form');
  const walletInput = document.getElementById('wallet-input') as HTMLInputElement;
  const saveWalletBtn = document.getElementById('save-wallet-btn');

  const showWalletForm = () => {
    walletForm?.classList.remove('hidden');
    walletInput?.focus();
  };

  linkWalletBtn?.addEventListener('click', showWalletForm);
  changeWalletBtn?.addEventListener('click', showWalletForm);

  saveWalletBtn?.addEventListener('click', async () => {
    const address = walletInput?.value?.trim();
    const walletError = document.getElementById('wallet-error');
    
    // Hide previous error
    walletError?.classList.add('hidden');
    
    if (address && isValidEthAddress(address)) {
      // Show loading state
      if (saveWalletBtn instanceof HTMLButtonElement) {
        saveWalletBtn.disabled = true;
        saveWalletBtn.textContent = 'SAVING...';
      }
      
      // Save wallet locally - will be sent with prediction
      currentWallet = address;
      callbacks.onStateChange(currentState);
      
      // Try to save to backend (non-blocking, just for profile storage)
      saveWalletToBackend(address).catch(() => {
        // Ignore backend save errors - wallet will still be sent with prediction
      });
    } else {
      walletInput?.classList.add('error');
      if (walletError) {
        walletError.textContent = 'Invalid address (must be 0x + 40 hex characters)';
        walletError.classList.remove('hidden');
      }
    }
  });

  // Test settle handler - active in dev mode OR with ?predict URL param
  const testSettleEnabled = import.meta.env.DEV || new URLSearchParams(window.location.search).has('predict');
  if (testSettleEnabled) {
    const testSettleBtn = document.getElementById('test-settle-btn');
    const testCloseInput = document.getElementById('test-close-input') as HTMLInputElement;
    
    testSettleBtn?.addEventListener('click', async () => {
      const closePrice = parseFloat(testCloseInput?.value || '0');
      if (!closePrice || closePrice <= 0) {
        alert('Enter a valid close price');
        return;
      }
      
      if (testSettleBtn instanceof HTMLButtonElement) {
        testSettleBtn.disabled = true;
        testSettleBtn.textContent = 'SETTLING...';
      }
      
      try {
        // Call settle API with test close price (test mode bypasses cron auth)
        const response = await fetch('/api/predictions/settle', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Test-Settle': 'true',
          },
          body: JSON.stringify({
            date: currentPrediction?.targetDate || getTomorrowDate(),
            testClose: closePrice,
          }),
        });
        
        if (response.ok) {
          // Refresh profile to get settled prediction
          const profile = await fetchUserProfile();
          currentPrediction = profile?.prediction || null;
          currentStats = profile?.stats || null;
          callbacks.onStateChange(currentState);
        } else {
          const err = await response.json();
          alert('Settle failed: ' + (err.error || 'Unknown error'));
          if (testSettleBtn instanceof HTMLButtonElement) {
            testSettleBtn.disabled = false;
            testSettleBtn.textContent = 'SETTLE';
          }
        }
      } catch (err) {
        alert('Settle failed: ' + err);
        if (testSettleBtn instanceof HTMLButtonElement) {
          testSettleBtn.disabled = false;
          testSettleBtn.textContent = 'SETTLE';
        }
      }
    });

    // Reset button handler
    const testResetBtn = document.getElementById('test-reset-btn');
    testResetBtn?.addEventListener('click', async () => {
      if (!confirm('Delete this prediction and result? You can make a new prediction after.')) {
        return;
      }
      
      if (testResetBtn instanceof HTMLButtonElement) {
        testResetBtn.disabled = true;
        testResetBtn.textContent = 'RESETTING...';
      }
      
      try {
        const token = await getAuthToken();
        const response = await fetch('/api/predictions/reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Test-Settle': 'true',
          },
          body: JSON.stringify({
            date: currentPrediction?.targetDate || getTomorrowDate(),
          }),
        });
        
        if (response.ok) {
          // Clear local state and refresh
          currentPrediction = null;
          currentStats = null;
          currentState = 'predict';
          callbacks.onStateChange(currentState);
        } else {
          const err = await response.json();
          alert('Reset failed: ' + (err.error || 'Unknown error'));
          if (testResetBtn instanceof HTMLButtonElement) {
            testResetBtn.disabled = false;
            testResetBtn.textContent = 'RESET PREDICTION';
          }
        }
      } catch (err) {
        alert('Reset failed: ' + err);
        if (testResetBtn instanceof HTMLButtonElement) {
          testResetBtn.disabled = false;
          testResetBtn.textContent = 'RESET PREDICTION';
        }
      }
    });
  }
}
